import { useEffect, useState } from "react";
import { Plus, Pencil, ShoppingCart, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Money, StatusPill, NativeSelect } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { todayIST, fmtDate, inr } from "@/lib/format";

const EMPTY_MAT = { name: "", unit: "kg", stock: 0, avg_cost: 0, reorder_level: 0 };

function MaterialDialog({ mat, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY_MAT);
  useEffect(() => { if (mat) setF({ ...EMPTY_MAT, ...mat }); }, [mat]);
  const save = async () => {
    try {
      const body = { ...f, stock: +f.stock, avg_cost: +f.avg_cost, reorder_level: +f.reorder_level };
      mat.id ? await api.put(`/materials/${mat.id}`, body) : await api.post("/materials", body);
      toast.success("Material saved");
      onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const field = (k, label, type = "text") => (
    <div className="space-y-1"><Label>{label}</Label><Input type={type} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`material-${k}-input`} /></div>
  );
  return (
    <Dialog open={!!mat} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="material-dialog">
        <DialogHeader><DialogTitle className="font-display">{mat?.id ? "Edit" : "Add"} raw material</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">{field("name", "Name")}</div>
          <div className="space-y-1"><Label>Unit</Label>
            <NativeSelect value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} data-testid="material-unit-select">
              {["kg", "g", "L", "pcs"].map((u) => <option key={u}>{u}</option>)}
            </NativeSelect>
          </div>
          {field("stock", "Current stock", "number")}
          {field("avg_cost", "Cost per unit (₹)", "number")}
          {field("reorder_level", "Reorder level", "number")}
        </div>
        <DialogFooter><Button className="bg-[#0F382C] hover:bg-[#134E3A]" onClick={save} data-testid="material-save-btn">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PurchaseDialog({ open, onClose, materials, onSaved }) {
  const [date, setDate] = useState(todayIST());
  const [supplier, setSupplier] = useState("Local market");
  const [status, setStatus] = useState("paid");
  const [rows, setRows] = useState([{ material_id: "", qty: "", rate: "" }]);
  useEffect(() => { if (open) setRows([{ material_id: materials[0]?.id || "", qty: "", rate: "" }]); }, [open, materials]);
  const upd = (i, k, v) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const total = rows.reduce((s, r) => s + (+r.qty || 0) * (+r.rate || 0), 0);
  const save = async () => {
    try {
      await api.post("/purchases", { date, supplier, payment_status: status, items: rows.filter((r) => r.material_id && +r.qty > 0).map((r) => ({ material_id: r.material_id, qty: +r.qty, rate: +r.rate || 0 })) });
      toast.success("Market purchase logged. Stock and average cost updated.");
      onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="purchase-dialog">
        <DialogHeader><DialogTitle className="font-display">Log market purchase</DialogTitle></DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1"><Label>Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="purchase-date" /></div>
          <div className="space-y-1"><Label>Supplier / shop</Label><Input value={supplier} onChange={(e) => setSupplier(e.target.value)} data-testid="purchase-supplier" /></div>
          <div className="space-y-1"><Label>Payment</Label>
            <NativeSelect value={status} onChange={(e) => setStatus(e.target.value)} data-testid="purchase-status"><option value="paid">Paid</option><option value="credit">On credit</option></NativeSelect>
          </div>
        </div>
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-[1fr_90px_100px_90px_36px] items-center gap-2">
              <NativeSelect value={r.material_id} onChange={(e) => upd(i, "material_id", e.target.value)} data-testid={`purchase-material-${i}`}>
                {materials.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.unit})</option>)}
              </NativeSelect>
              <Input type="number" placeholder="Qty" value={r.qty} onChange={(e) => upd(i, "qty", e.target.value)} data-testid={`purchase-qty-${i}`} />
              <Input type="number" placeholder="Rate ₹" value={r.rate} onChange={(e) => upd(i, "rate", e.target.value)} data-testid={`purchase-rate-${i}`} />
              <span className="text-right font-mono text-sm">{inr((+r.qty || 0) * (+r.rate || 0))}</span>
              <Button variant="ghost" size="icon" onClick={() => setRows(rows.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setRows([...rows, { material_id: materials[0]?.id || "", qty: "", rate: "" }])} data-testid="purchase-add-row"><Plus className="mr-1 h-3 w-3" />Add item</Button>
        </div>
        <DialogFooter className="items-center gap-4">
          <div className="font-mono text-lg font-semibold" data-testid="purchase-total">Total {inr(total)}</div>
          <Button className="bg-[#0F382C] hover:bg-[#134E3A]" onClick={save} data-testid="purchase-save-btn">Save purchase</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Materials() {
  const [materials, setMaterials] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [edit, setEdit] = useState(null);
  const [buy, setBuy] = useState(false);
  const load = () => {
    api.get("/materials").then((r) => setMaterials(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/purchases").then((r) => setPurchases(r.data)).catch(() => {});
  };
  useEffect(load, []);
  const stockValue = materials.reduce((s, m) => s + Math.max(m.stock, 0) * m.avg_cost, 0);

  return (
    <div data-testid="materials-page">
      <PageHeader eyebrow="Raw materials" title="Stock & market purchases" subtitle={`Stock value on hand: ${inr(stockValue)}. Average cost updates with every purchase, so your recipe costs stay accurate.`}>
        <Button variant="outline" onClick={() => setEdit({})} data-testid="add-material-btn"><Plus className="mr-1 h-4 w-4" />Material</Button>
        <Button className="rounded-full bg-[#D96B43] hover:bg-[#c55d38]" onClick={() => setBuy(true)} data-testid="log-purchase-btn"><ShoppingCart className="mr-2 h-4 w-4" />Log market purchase</Button>
      </PageHeader>
      <Tabs defaultValue="stock">
        <TabsList className="bg-[#FAF4E8]"><TabsTrigger value="stock" data-testid="tab-stock">Stock</TabsTrigger><TabsTrigger value="purchases" data-testid="tab-purchases">Purchases</TabsTrigger></TabsList>
        <TabsContent value="stock">
          <div className="rounded-2xl border border-[#E7E2D6] bg-white p-2 sm:p-4 overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Material</TableHead><TableHead className="text-right">Stock</TableHead><TableHead className="text-right">Avg cost</TableHead><TableHead className="text-right">Value</TableHead><TableHead className="text-right">Reorder at</TableHead><TableHead /></TableRow></TableHeader>
              <TableBody>
                {materials.map((m) => (
                  <TableRow key={m.id} data-testid={`material-row-${m.id}`}>
                    <TableCell className="font-medium">{m.name} {m.stock <= m.reorder_level && <StatusPill status="low" label="Low" />}</TableCell>
                    <TableCell className="text-right font-mono">{Number(m.stock).toFixed(2)} {m.unit}</TableCell>
                    <TableCell className="text-right"><Money value={m.avg_cost} />/{m.unit}</TableCell>
                    <TableCell className="text-right"><Money value={Math.max(m.stock, 0) * m.avg_cost} /></TableCell>
                    <TableCell className="text-right font-mono">{m.reorder_level} {m.unit}</TableCell>
                    <TableCell className="text-right"><Button variant="ghost" size="icon" onClick={() => setEdit(m)} data-testid={`edit-material-${m.id}`}><Pencil className="h-4 w-4" /></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
        <TabsContent value="purchases">
          <div className="rounded-2xl border border-[#E7E2D6] bg-white p-2 sm:p-4 overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Supplier</TableHead><TableHead>Items</TableHead><TableHead>Payment</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
              <TableBody>
                {purchases.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No purchases yet</TableCell></TableRow>}
                {purchases.map((p) => (
                  <TableRow key={p.id} data-testid={`purchase-row-${p.id}`}>
                    <TableCell>{fmtDate(p.date)}</TableCell>
                    <TableCell>{p.supplier}</TableCell>
                    <TableCell className="text-sm">{p.items.map((i) => `${i.name} ${i.qty}${i.unit} @ ₹${i.rate}`).join(", ")}</TableCell>
                    <TableCell><StatusPill status={p.payment_status} /></TableCell>
                    <TableCell className="text-right"><Money value={p.total} className="font-semibold" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
      <MaterialDialog mat={edit} onClose={() => setEdit(null)} onSaved={load} />
      <PurchaseDialog open={buy} onClose={() => setBuy(false)} materials={materials} onSaved={load} />
    </div>
  );
}
